import React, { useState } from 'react';
import { hapticFeedback } from '../utils/haptic';

interface FrequencyInputModalProps {
  isOpen: boolean;
  onFrequencySet: (frequency: number) => void;
  onCancel: () => void;
}

export const FrequencyInputModal: React.FC<FrequencyInputModalProps> = ({
  isOpen,
  onFrequencySet,
  onCancel
}) => {
  const [inputValue, setInputValue] = useState('4000');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const validateFrequency = (freq: number): boolean => {
    if (isNaN(freq) || freq < 20 || freq > 20000) {
      setError('Please enter a frequency between 20 and 20,000 Hz');
      return false;
    }
    setError('');
    return true;
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (error) setError(''); // Clear error when user types
  };

  const handlePresetClick = (freq: number) => {
    setInputValue(freq.toString());
    setError('');
    hapticFeedback.buttonTap();
  };

  const handleSubmit = () => {
    const freq = parseInt(inputValue);
    if (validateFrequency(freq)) {
      hapticFeedback.buttonTap();
      onFrequencySet(freq);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl animate-slide-in relative">
        <h2 className="text-2xl font-bold text-purple-700 mb-4 text-center">
          Enter Your Frequency
        </h2>
        
        {/* Direct Number Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Frequency (Hz)
          </label>
          <input
            type="number"
            min="20"
            max="20000"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="e.g. 4000"
            className={`w-full p-3 border-2 rounded-lg text-center text-2xl font-bold focus:outline-none transition-colors ${
              error 
                ? 'border-red-300 focus:border-red-500' 
                : 'border-purple-200 focus:border-purple-500'
            }`}
            autoFocus
          />
          {error && (
            <p className="text-red-500 text-sm mt-1 text-center">{error}</p>
          )}
          <p className="text-xs text-gray-500 mt-1 text-center">
            Most tinnitus frequencies are between 2000-8000 Hz
          </p>
        </div>

        {/* Quick Presets */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 mb-2">Or choose common frequencies:</p>
          <div className="grid grid-cols-3 gap-2">
            {[2000, 4000, 6000, 8000, 10000, 12000].map(freq => (
              <button
                key={freq}
                onClick={() => handlePresetClick(freq)}
                className={`py-2 px-3 rounded font-medium transition-colors tap-highlight-none ${
                  parseInt(inputValue) === freq
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                    : 'bg-purple-100 hover:bg-purple-200 text-purple-700 active:bg-purple-200'
                }`}
              >
                {freq}
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors tap-highlight-none"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-bold transition-all duration-200 shadow-lg tap-highlight-none active:scale-95"
          >
            Start Therapy
          </button>
        </div>
      </div>
    </div>
  );
};